import type { SavedPlan } from "../types";

const HISTORY_KEY = "futures-risk.history.v1";
const MAX_HISTORY = 80;

function isSavedPlan(value: unknown): value is SavedPlan {
  if (!value || typeof value !== "object") return false;
  const plan = value as Partial<SavedPlan>;
  return typeof plan.id === "string"
    && typeof plan.createdAt === "number"
    && typeof plan.updatedAt === "number"
    && typeof plan.inputs?.symbol === "string"
    && typeof plan.result?.quantity === "number"
    && typeof plan.rules?.quantityStep === "number";
}

export function loadHistory(): SavedPlan[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter(isSavedPlan).slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

function persist(plans: SavedPlan[]): void {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(plans.slice(0, MAX_HISTORY)));
}

export function savePlan(plan: SavedPlan): SavedPlan[] {
  const plans = loadHistory();
  const index = plans.findIndex((item) => item.id === plan.id);
  if (index >= 0) plans.splice(index, 1);
  plans.unshift(plan);
  persist(plans);
  return plans;
}

export function removePlan(id: string): SavedPlan[] {
  const plans = loadHistory().filter((item) => item.id !== id);
  persist(plans);
  return plans;
}

export function clearHistory(): void {
  localStorage.removeItem(HISTORY_KEY);
}

export function exportHistory(plans: SavedPlan[]): void {
  const blob = new Blob([JSON.stringify({ version: 1, exportedAt: Date.now(), plans }, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `合约风险方案-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function importHistory(file: File): Promise<SavedPlan[]> {
  const parsed = JSON.parse(await file.text()) as unknown;
  const candidates = Array.isArray(parsed)
    ? parsed
    : (parsed && typeof parsed === "object" && "plans" in parsed)
      ? (parsed as { plans: unknown }).plans
      : [];
  if (!Array.isArray(candidates)) throw new Error("文件中没有可导入的方案");
  const valid = candidates.filter(isSavedPlan);
  if (valid.length === 0) throw new Error("没有识别到有效的历史方案");
  const merged = [...valid, ...loadHistory()]
    .filter((item, index, all) => all.findIndex((other) => other.id === item.id) === index)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_HISTORY);
  persist(merged);
  return merged;
}
